import { Storage } from 'aws-amplify';
import { FoundFile } from '../types';
import Honeybadger from '@honeybadger-io/js';
import { v4 as uuidV4 } from 'uuid';

type Destination = 'kindbody' | 'personal';

type DocumentKey = {
  type: 'file' | 'folder';
  path: string;
  destination: Destination;
};

const KINDBODY_DOCS_PATH = 'documents/kindbody/';
const USER_DOCS_PATH = 'documents/personal/';

const definedKeys: Array<DocumentKey> = [
  {
    type: 'folder',
    path: KINDBODY_DOCS_PATH,
    destination: 'kindbody'
  },
  {
    type: 'folder',
    path: USER_DOCS_PATH,
    destination: 'personal'
  }
];

type Documents = {
  kindbodyDocs: Array<string>;
  personalDocs: Array<string>;
  error?: Error;
};

const s3Config = { level: 'private' };

const getDocument = async (path: string, destination: Destination): Promise<FoundFile> => {
  const file = await Storage.get(path, s3Config);
  // @ts-ignore
  const decoded = decodeURI(file);
  let fileName = 'Unknown Name';
  const fileNameRegex = /(?!.*\/)([^?]*)/gm;
  const fileNameMatch = fileNameRegex.exec(decoded);
  if (fileNameMatch && fileNameMatch[0]) {
    fileName = fileNameMatch[0];
  }

  return {
    // @ts-ignore
    path: file,
    s3Path: path,
    fileName,
    destination
  };
};

const getDocuments = async (keys: Array<DocumentKey>): Promise<Array<FoundFile>> => {
  try {
    let docs: FoundFile[] = [];
    for (let i = 0; i < keys.length; i++) {
      const { type, path, destination } = keys[i];

      switch (type) {
        case 'file':
          const file = await getDocument(path, destination);
          // @ts-ignore
          docs = docs.concat({ path: file, destination });
          continue;
        case 'folder':
          try {
            const foundFiles = await Storage.list(path, s3Config);

            // @ts-ignore
            const mappedFound = foundFiles.map(file => {
              return {
                type: 'file',
                path: file.key,
                destination
              };
            });
            const folderFiles = await getDocuments(mappedFound);
            docs = docs.concat(folderFiles);
            continue;
          } catch (e) {
            // If storage list does not work It's because cognito credentials are not set.
            // It happens when patient is logged by Google or SSO
            Honeybadger.notify(e, 'Storage Error');
          }

        default:
          continue;
      }
    }
    return docs;
  } catch (e) {
    console.error(e);
  }
};

export const getDocumentsAsync = async (): Promise<Documents> => {
  const defaultDocuments = { kindbodyDocs: [], personalDocs: [] };
  try {
    const docs = await getDocuments(definedKeys);
    return docs.reduce((docObj, doc) => {
      const { path, destination } = doc;
      const { kindbodyDocs, personalDocs } = docObj;

      switch (destination) {
        case 'kindbody':
          return {
            ...docObj,
            // @ts-ignore
            kindbodyDocs: kindbodyDocs.concat(path)
          };
        case 'personal':
          return {
            ...docObj,
            // @ts-ignore
            personalDocs: personalDocs.concat(path)
          };
        default:
          return docObj;
      }
    }, defaultDocuments);
  } catch (error) {
    Honeybadger.notify(error);
    return {
      ...defaultDocuments,
      error: new Error('Could not fetch documents')
    };
  }
};

interface FileName {
  name: string;
  uuid: string;
}

export const putPersonalDocAsync = async (file: File): Promise<boolean | FileName> => {
  try {
    // @ts-ignore
    const { name, contentType } = file;
    const uuid = uuidV4();
    await Storage.put(`${USER_DOCS_PATH}${uuid}`, file, {
      ...s3Config,
      contentType
    });

    return { name, uuid };
  } catch (error) {
    Honeybadger.notify(error, { name: 'DocumentUploadError' });
    return false;
  }
};

export const deleteDocAsync = async (path: string): Promise<boolean> => {
  try {
    await Storage.remove(path, s3Config);
    return true;
  } catch (error) {
    Honeybadger.notify(error, 'DocumentDeleteError');
    return false;
  }
};
